import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import LgmButton from '../LgmButton';

// ---------------------------------------------------------------------------
// CancelLinkConfirmDialog — issue #622, Sub-task 9.5 ("Cancel's confirmation
// is simple")
// ---------------------------------------------------------------------------

export interface CancelLinkConfirmDialogProps {
  sourceLabel: string;
  stagedCount: number;
  /** Backdrop click / Escape / the dialog's own "Keep editing" button — never discards. */
  onKeepEditing: () => void;
  /** Only the explicit "Discard" button reaches this — see LinkToSourceModal.tsx's `handleCloseRequest`. */
  onDiscard: () => void;
}

/**
 * A plain "discard your selections?" dialog (Sub-task 9.5) — the sibling of
 * `LinkConfirmDialog`'s much more involved Link confirmation, deliberately
 * kept simple per the sub-task's own acceptance criteria. Only ever shown
 * when there is something to lose (`stagedCount > 0` — see
 * `LinkToSourceModal.tsx`'s `handleCloseRequest`, which skips straight to
 * `onClose` when the review list is already empty).
 *
 * Backdrop click and Escape both route to `onKeepEditing`, matching
 * `BreakLinkConfirmDialog`'s "dismiss never commits/discards" convention —
 * closing this dialog itself never discards anything; only the explicit
 * "Discard" button does.
 */
const CancelLinkConfirmDialog: React.FC<CancelLinkConfirmDialogProps> = ({
  sourceLabel,
  stagedCount,
  onKeepEditing,
  onDiscard,
}) => (
  <Dialog
    open
    onClose={onKeepEditing}
    sx={{ zIndex: 20000 }}
    slotProps={{ paper: { sx: { minWidth: 360, maxWidth: 480 } } }}
    aria-labelledby="cancel-link-dialog-title">
    <DialogTitle
      id="cancel-link-dialog-title"
      sx={{ fontWeight: 600, fontSize: '1rem' }}>
      Discard your selections?
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary">
        {stagedCount} {sourceLabel} record{stagedCount === 1 ? '' : 's'}{' '}
        selected for review will be discarded. Nothing has been linked yet —
        this cannot be undone once you close this modal.
      </Typography>
    </DialogContent>
    <DialogActions>
      <LgmButton variant="outlined" onClick={onKeepEditing}>
        Keep editing
      </LgmButton>
      <LgmButton color="danger" onClick={onDiscard}>
        Discard
      </LgmButton>
    </DialogActions>
  </Dialog>
);

export default CancelLinkConfirmDialog;
